import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { useHistory } from "react-router-dom";

import { Breadcrumb, HyphenIcon } from "@transfr-inc/dashboard-components";
import { PageHeader } from "@transfr-inc/dashboard-components/layouts";

import { CopyButton } from "@components/copy-button";
import { ArchivedBadge, DemoBadge } from "@components/custom/content";
import { useClassroomInstructors } from "@hooks/queries";
import { createClassroomsLink } from "@lib/utils/navigation.utils";

import "./classroom.header.scss";

export const ClassroomHeader = ({ classroom, breadcrumbLabel, children }) => {
  const history = useHistory();
  // If the history length is 1,
  // it means it's the first page in the history
  const isNewWindow = history.length === 1;

  const {
    data: instructors,
    isFetching,
    isError,
  } = useClassroomInstructors(classroom.classroomId);

  const instructorList = useMemo(() => {
    let instructorItems = (
      <li className="has-separator">
        <HyphenIcon />
      </li>
    );

    if (isFetching) {
      instructorItems = (
        <li className="has-separator">
          <Skeleton height={18} width={80} />
        </li>
      );
    } else if (instructors?.length > 0) {
      instructorItems = instructors.map(
        ({ firstName, lastName, userId }, index) => (
          <li
            key={`${userId}-${index}`}
            className={clsx(
              "instructor-item",
              index == instructors.length - 1 && "has-separator"
            )}
          >
            {`${firstName} ${lastName}`}
            {index < instructors.length - 1 && (
              <FontAwesomeIcon className="plus-icon" icon={["far", "plus"]} />
            )}
          </li>
        )
      );
    }

    return (
      <>
        <li className="has-padding-left">Instructors:</li>
        {instructorItems}
      </>
    );
  }, [instructors, isFetching, isError]);

  const subTitle = (
    <ul className="metadata">
      <li className="classroom-code has-separator">
        Code: {classroom.code}
        <CopyButton value={classroom.code} />
      </li>
      {instructorList}
      {classroom.isDemo && (
        <li className="has-padding-left">
          <DemoBadge className="classroom-demo-badge" />
        </li>
      )}
      {classroom.isArchived && (
        <li className="has-padding-left">
          <ArchivedBadge />
        </li>
      )}
    </ul>
  );

  const onBreadcrumbClick = () => {
    if (isNewWindow || breadcrumbLabel === "All Classrooms") {
      history.push(createClassroomsLink());
    } else {
      history.goBack();
    }
  };

  return (
    <PageHeader
      className="classroom-details-header"
      title={classroom.name}
      subTitle={subTitle}
      breadcrumb={
        <Breadcrumb
          label={isNewWindow ? "All Classrooms" : breadcrumbLabel}
          onClick={onBreadcrumbClick}
        />
      }
    >
      {children}
    </PageHeader>
  );
};

ClassroomHeader.propTypes = {
  classroom: PropTypes.object.isRequired,
  breadcrumbLabel: PropTypes.string,
  children: PropTypes.node,
};
